import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const FaqPage = () => {
  const [open, setOpen] = useState(0);
  const [items, setItems] = useState(null);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/faqs")
      .then((r) => setItems(r.data.items || []))
      .catch(() => {
        setItems(Array.from({ length: 8 }, (_, i) => ({ q: t(`faq.items.${i}.q`), a: t(`faq.items.${i}.a`) })));
      });
  }, []);

  const text = (it, k) => {
    if (lang === "en" && it[`${k}_en`]) return it[`${k}_en`];
    if (lang === "es" && it[`${k}_es`]) return it[`${k}_es`];
    return it[k];
  };

  return (
    <div data-testid="faq-page" className="pt-36 sm:pt-40 pb-24">
      <Seo
        title={t("seo.faqTitle")}
        siteName="ROMACASA"
        description={t("seo.faqDesc")}
      />
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-14">
          <Eyebrow>{t("faq.eye")}</Eyebrow>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mt-4 mb-6">{t("faq.title")}</h1>
          <p className="text-[#5B6670] font-light text-base sm:text-lg leading-relaxed">{t("faq.sub")}</p>
        </Reveal>

        {items === null ? (
          <div className="py-16 text-center text-[#5B6670] font-light" data-testid="faq-loading">{t("blog.loading")}</div>
        ) : (
          <div data-testid="faq-list">
            {items.map((it, i) => (
              <Reveal key={i} delay={Math.min(i, 6) * 0.04}>
                <div className="border-b border-[#E0E3E6]">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  >
                    <span className={`font-serif-d text-xl sm:text-2xl font-medium transition-colors ${open === i ? "text-black" : "text-[#2B323A] group-hover:text-black"}`}>
                      {text(it, "q")}
                    </span>
                    <ChevronDown size={20} className={`shrink-0 text-black transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    data-testid={`faq-answer-${i}`}
                    className={`overflow-hidden transition-all duration-500 ${open === i ? "max-h-96 pb-7 opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <p className="text-[#5B6670] font-light leading-[1.85] max-w-2xl">{text(it, "a")}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.1} className="mt-14 text-center">
          <Link
            to="/contatti"
            data-testid="faq-contact-button"
            className="inline-block bg-black hover:bg-[#2E2E2E] text-white px-8 py-4 text-sm font-medium transition-colors duration-300"
          >
            {t("nav.contatti")}
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default FaqPage;
